import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    SpinButton,
    SpinButtonChangeEvent,
    SpinButtonOnChangeData,
} from '@fluentui/react-components';
import { Table20Regular } from '@fluentui/react-icons';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TablePlugin as LexicalTablePlugin } from '@lexical/react/LexicalTablePlugin';
import {
    $isTableCellNode,
    $isTableSelection,
    INSERT_TABLE_COMMAND,
    TableNode,
    TableRowNode,
} from '@lexical/table';
import { CustomTableCellNode, $isCustomTableCellNode } from '../nodes/CustomTableCellNode';
import { $getSelection } from 'lexical';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

export { INSERT_TABLE_COMMAND };

type InsertTableDialogProps = {
  onClose: () => void;
};

export function InsertTableDialog({ onClose }: InsertTableDialogProps): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [rows, setRows] = useState(3);
  const [columns, setColumns] = useState(3);

  const handleConfirm = useCallback(() => {
    if (rows >= 1 && rows <= 500 && columns >= 1 && columns <= 50) {
      // Focus the editor first to ensure we have a valid selection
      editor.focus();
      editor.dispatchCommand(INSERT_TABLE_COMMAND, {
        rows: String(rows),
        columns: String(columns),
      });
      onClose();
    }
  }, [editor, rows, columns, onClose]);

  const handleRowsChange = useCallback(
    (_ev: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
      if (data.value !== undefined && data.value !== null) {
        setRows(Math.max(1, Math.min(500, data.value)));
      } else if (data.displayValue !== undefined) {
        const parsed = parseInt(data.displayValue, 10);
        if (!isNaN(parsed)) {
          setRows(Math.max(1, Math.min(500, parsed)));
        }
      }
    },
    []
  );

  const handleColumnsChange = useCallback(
    (_ev: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
      if (data.value !== undefined && data.value !== null) {
        setColumns(Math.max(1, Math.min(50, data.value)));
      } else if (data.displayValue !== undefined) {
        const parsed = parseInt(data.displayValue, 10);
        if (!isNaN(parsed)) {
          setColumns(Math.max(1, Math.min(50, parsed)));
        }
      }
    },
    []
  );

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleConfirm, onClose]);

  return (
    <Dialog open onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className="table-dialog-surface">
        <DialogBody>
          <DialogTitle className="table-dialog-title">
            <Table20Regular className="table-dialog-icon" />
            Infoga tabell
          </DialogTitle>
          <DialogContent className="table-dialog-content">
            <div className="table-input-row">
              <Field label="Rader" size="small" className="table-input-field">
                <SpinButton
                  size="small"
                  value={rows}
                  min={1}
                  max={500}
                  onChange={handleRowsChange}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </Field>
              <Field label="Kolumner" size="small" className="table-input-field">
                <SpinButton
                  size="small"
                  value={columns}
                  min={1}
                  max={50}
                  onChange={handleColumnsChange}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Avbryt
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={rows < 1 || rows > 500 || columns < 1 || columns > 50}
            >
              Infoga tabell
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default function TablePlugin(): React.JSX.Element {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([TableNode, TableRowNode, CustomTableCellNode])) {
      throw new Error(
        'TablePlugin: TableNode, TableRowNode, or CustomTableCellNode is not registered on editor'
      );
    }
  }, [editor]);

  // Add selection state observer for visual feedback
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        // Clear previous selection classes
        const rootElement = editor.getRootElement();
        if (rootElement) {
          rootElement.querySelectorAll('td.selected, th.selected').forEach((el) => {
            el.classList.remove('selected');
          });

          // Apply selection class to table cells
          if ($isTableSelection(selection)) {
            const nodes = selection.getNodes();
            nodes.forEach((node) => {
              if ($isTableCellNode(node) || $isCustomTableCellNode(node)) {
                const element = editor.getElementByKey(node.getKey());
                if (element) {
                  element.classList.add('selected');
                }
              }
            });
          }
        }
      });
    });
  }, [editor]);

  return <LexicalTablePlugin hasCellMerge={true} hasCellBackgroundColor={true} />;
}
